import { IconButton, SingleSelectDropdown, TextInput } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useState } from "react";
import { hatcheryDropdownModes } from "@/app/theme";
import styles from "./AxisFilterBar.module.css";

// 한 조회 축의 정의다. options 가 있으면 닫힌 집합이다. 그래서 값도 드롭다운으로 고르게 한다.
// 이렇게 하면 오타 때문에 결과가 비는 일이 없다.
export type AxisFilterFieldDef<K extends string> = {
  key: K;
  label: string;
  options?: readonly { value: string; label: string }[];
};

export type AxisFilterChip<K extends string> = {
  key: K;
  value: string;
};

/**
 * 축 선택 · 값 입력 · [추가] 로 조회 조건을 칩으로 쌓는 필터 입력 줄이다.
 *
 * 모든 축을 펼치지 않고, 쓰는 축만 칩으로 남긴다. 한 축에는 값이 하나뿐이다. 그래서 같은 축을
 * 다시 추가하면 기존 값을 교체한다. 목록 API 들이 축마다 값 하나만 받기 때문이다.
 *
 * 확정된 칩은 호출부(=URL)가 소유하고 AxisFilterChips 가 그린다. 이 컴포넌트는 아직 확정하지
 * 않은 입력만 가진다. 같은 chips 를 받는 이유는 같은 축 교체 규칙 하나뿐이다.
 */
export function AxisFilterBar<K extends string>({
  fields,
  chips,
  onChange,
}: {
  // 고를 수 있는 축이다. 호출부가 상황에 따라 거른 뒤 전달한다(예: 다른 필터가 이미 그 축을 소유할 때).
  fields: readonly AxisFilterFieldDef<K>[];
  chips: readonly AxisFilterChip<K>[];
  onChange: (next: readonly AxisFilterChip<K>[]) => void;
}) {
  const [field, setField] = useState<K>(fields[0].key);
  const [value, setValue] = useState("");

  // 고른 축이 목록에서 빠질 수 있다. 그때는 남은 첫 축으로 바꾼다. 그래서 값 입력이 없어진 축을 가리키지 않는다.
  useEffect(() => {
    if (!fields.some((candidate) => candidate.key === field)) {
      setField(fields[0].key);
      setValue("");
    }
  }, [fields, field]);

  const fieldDef = useMemo(
    () => fields.find((candidate) => candidate.key === field),
    [fields, field],
  );

  const fieldOptions = useMemo(
    () => fields.map((candidate) => ({ value: candidate.key, label: candidate.label })),
    [fields],
  );

  const addChip = useCallback(
    (rawValue: string) => {
      const trimmed = rawValue.trim();
      if (trimmed === "") {
        return;
      }
      onChange([...chips.filter((chip) => chip.key !== field), { key: field, value: trimmed }]);
      setValue("");
    },
    [chips, field, onChange],
  );

  const handleFieldChange = useCallback((next: string) => {
    setField(next as K);
    setValue("");
  }, []);

  const closedSetOptions = fieldDef?.options;

  return (
    <div className={styles.bar}>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="Filter"
        value={field}
        options={fieldOptions}
        onValueChange={handleFieldChange}
      />
      {/* 래퍼가 값 컨트롤의 검색 폭 규약(rules.md §17)을 적용하고, 컨트롤은 그 칸을 채운다. c6 는
          className 폭을 무시한다. 닫힌 집합 드롭다운도 같은 칸에 둔다. 그래서 축을 바꿔도 줄 배치가 바뀌지 않는다. */}
      <span className={styles.value}>
        {closedSetOptions !== undefined ? (
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.default}
            label="값"
            value={value}
            options={closedSetOptions}
            onValueChange={addChip}
            placeholder="값을 선택해 주세요."
            fullWidth
          />
        ) : (
          <TextInput
            aria-label={`${fieldDef?.label ?? field} 값`}
            fullWidth
            value={value}
            placeholder="값을 입력하고 Enter 를 눌러 주세요."
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                addChip(value);
              }
            }}
          />
        )}
      </span>
      {/* 닫힌 집합 축은 고르는 즉시 확정된다. 그래서 [추가] 버튼이 필요 없다. */}
      <IconButton
        text="추가"
        type="button"
        variant="surface"
        disabled={closedSetOptions !== undefined || value.trim() === ""}
        onClick={() => addChip(value)}
      />
    </div>
  );
}
